import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import MatterBreadcrumbsMedChronAsset from '@/matter/components/matter-breadcrumbs/MatterBreadcrumbsMedChronAsset'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { Document } from '@/assets/icons'
import { timestampMsToDateTimeString } from '@/util/time'
import { MedChronJobActions } from '@/medical-chronology/jobs/store/slice'
import { MedChronJobStatus } from '@/medical-chronology/jobs/store/schemas'
import MedChronJobDownloadButton from '@/medical-chronology/jobs/components/job-actions/MedChronJobDownloadButton'

/**
 * Download-only access to chronology reports generated before the living chronology.
 * Deprecated history: reports can be downloaded but never viewed or regenerated.
 */
export default function LegacyReportsPage() {
  const dispatch = useAppDispatch()
  const { jobs, jobsLoading, jobsError } = useAppSelector((state: RootState) => state.medChronJobState)
  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)

  useEffect(() => {
    dispatch(MedChronJobActions.getMedChronJobs())
  }, [dispatch])

  const reports = useMemo(() => {
    let array = Object.values(jobs).filter((job) => job.status === MedChronJobStatus.DONE)
    if (activeMatterId) {
      array = array.filter((job) => job.matter_id === activeMatterId)
    }
    return array.sort((a, b) => b.updated_at - a.updated_at)
  }, [jobs, activeMatterId])

  return (
    <div className="grow flex flex-col w-full h-full overflow-hidden">
      <MatterBreadcrumbsMedChronAsset />

      <div className="flex-1 min-h-0 overflow-y-auto p-4 lg:p-6">
        <div className="max-w-[946px] mx-auto flex flex-col gap-4">
          <div className="px-4 py-3 bg-brand-neutral-100 border border-brand-neutral-200 rounded-md">
            <p className="text-sm text-brand-neutral-950 font-medium">Legacy chronology reports are download-only.</p>
            <p className="text-sm text-brand-neutral-600">
              These reports were generated before the living chronology and are no longer updated. The Medical Chronology page is now the single, up-to-date
              chronology for this matter.
            </p>
          </div>

          {jobsLoading && (
            <div className="flex items-center justify-center min-h-52">
              <CircularProgressContinuousSized size={16} thickness={7} />
            </div>
          )}

          {!jobsLoading && jobsError && <p className="p-2 text-red-700 text-sm text-center">There was an error loading the legacy reports.</p>}

          {!jobsLoading && !jobsError && (
            <div className="border rounded-md bg-white overflow-hidden">
              <div className="flex items-center border-b bg-brand-neutral-50 px-3 py-2.5 text-sm font-medium gap-3">
                <span className="flex-1">Name</span>
                <span className="w-48">Created</span>
                <span className="w-16 text-right" />
              </div>
              {reports.map((job) => (
                <div key={job.id} className="flex items-center border-b last:border-b-0 px-3 py-2.5 text-sm gap-3">
                  <div className="flex-1 min-w-0 flex items-center gap-2">
                    <Document className="w-5 h-5 text-brand-neutral-700 shrink-0" />
                    <span className="truncate" title={job.name}>
                      {job.name}
                    </span>
                  </div>
                  <span className="w-48 shrink-0 text-brand-neutral-600">{timestampMsToDateTimeString(job.created_at)}</span>
                  <span className="w-16 shrink-0 flex justify-end">
                    <MedChronJobDownloadButton jobId={job.id} jobStatus={job.status} jobName={job.name} />
                  </span>
                </div>
              ))}
              {reports.length === 0 && <div className="px-3 py-6 text-sm text-brand-neutral-600 text-center">No legacy reports for this matter.</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
